/**
 * Granskning of one document's text (ADR-0102 addendum 2): its suggestions
 * grouped per changed node with accept / reject (per suggestion or per
 * group), stale ones marked, each with its comment threads; then the
 * comments on unchanged nodes, the document and changes no longer listed. Suggestion text and comments are contract content:
 * display only.
 */

import { useState } from 'react';
import type { ActionLog, SuggestionInfo } from '@hyranai/regta-document-ir';
import { tenantApi, type ReviewThread } from '../../../api.js';
import { ChangeThreads, CommentComposer, ReviewThreadCard } from '../../../components/beta-review/ReviewThreads.js';
import { suggestionGroups, type ThreadAssignment } from '../../../components/beta-review/review-model.js';
import { authorLabel, describeEntry, entryText, formatAt } from './model.js';

/** `changeKey`: the change list entry the composer opens under, when the seq sits in more than one. */
export type CommentAnchor = { kind: 'action'; seq: number; changeKey?: string } | { kind: 'node'; address: string } | { kind: 'document' };

const STATE_LABEL: Record<SuggestionInfo['state'], string> = {
  proposed: 'Förslag',
  stale: 'Inaktuellt',
  accepted: 'Godtaget',
  rejected: 'Avvisat',
};
const STATE_CLS: Record<SuggestionInfo['state'], string> = {
  proposed: 'border-red-200 bg-red-50 text-red-800',
  stale: 'border-amber-300 bg-amber-50 text-amber-900',
  accepted: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  rejected: 'border-stone-200 bg-stone-100 text-stone-500',
};

/** Key of a suggestion in the change lists (`assignThreads`, highlight). */
export const suggestionKey = (seq: number): string => `s:${seq}`;

const truncate = (s: string, n = 160): string => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

export function ReviewSidebar({
  contractId,
  attachmentId,
  suggestions,
  log,
  assigned,
  highlightKey,
  names,
  actorId,
  decidable,
  suggesting = false,
  commentable,
  busy,
  composer,
  onComposer,
  onDecide,
  onShow,
  onThreadsChanged,
}: {
  contractId: string;
  attachmentId: string | null;
  suggestions: readonly SuggestionInfo[];
  log: ActionLog | null;
  /** This document's threads placed with their change (`assignThreads`). */
  assigned: ThreadAssignment<ReviewThread>;
  /** The change the document's badge or "Kommentera" pointed at. */
  highlightKey: string | null;
  names: ReadonlyMap<string, string>;
  actorId: string | null;
  /** Accept / reject: anyone who can view, while the contract is draft, pending or in_review. */
  decidable: boolean;
  /** New text changes become suggestions (in_review). */
  suggesting?: boolean;
  commentable: boolean;
  busy: boolean;
  composer: CommentAnchor | null;
  onComposer: (anchor: CommentAnchor | null) => void;
  onDecide: (seqs: number[], decision: 'accept' | 'reject') => void;
  onShow: (address: string) => void;
  onThreadsChanged: () => Promise<void> | void;
}) {
  const [showDecided, setShowDecided] = useState(false);
  const open = suggestions.filter((s) => s.open);
  const decided = suggestions.filter((s) => !s.open);
  const entry = (seq: number) => log?.entries.find((e) => e.seq === seq) ?? null;
  const composingOn = (seq: number) =>
    composer?.kind === 'action' && composer.seq === seq && (composer.changeKey == null || composer.changeKey === suggestionKey(seq));
  const targeted = (seq: number) => highlightKey === suggestionKey(seq) || composingOn(seq);
  const decidedOpen = showDecided || decided.some((s) => targeted(s.seq));
  const nodeThreads = assigned.nodes;
  const docThreads = assigned.document;
  const orphanActionThreads = assigned.actions;

  const create = async (anchor: Exclude<CommentAnchor, { kind: 'action' }>, body: string) => {
    await tenantApi.review_comments.create({
      contract_id: contractId,
      attachment_id: attachmentId,
      anchor_kind: anchor.kind,
      anchor: anchor.kind === 'node' ? anchor.address : null,
      kind: 'comment',
      body,
    });
    onComposer(null);
    await onThreadsChanged();
  };
  const composerFor = (anchor: Exclude<CommentAnchor, { kind: 'action' }>) =>
    composer &&
    composer.kind === anchor.kind &&
    (anchor.kind !== 'node' || (composer.kind === 'node' && composer.address === anchor.address)) ? (
      <div className="mt-2">
        <CommentComposer autoFocus onCancel={() => onComposer(null)} onSubmit={(body) => create(anchor, body)} />
      </div>
    ) : null;
  const threadList = (list: readonly ReviewThread[], heading?: (t: ReviewThread) => string | undefined) =>
    list.length > 0 ? (
      <ul className="mt-2 flex flex-col gap-1.5">
        {list.map((t) => (
          <ReviewThreadCard
            key={t.id}
            thread={t}
            contractId={contractId}
            names={names}
            writable={commentable}
            heading={heading?.(t)}
            onShow={t.anchor_kind === 'node' && t.anchor ? () => onShow(t.anchor!) : undefined}
            onChanged={onThreadsChanged}
          />
        ))}
      </ul>
    ) : null;

  const suggestionItem = (s: SuggestionInfo) => {
    const e = entry(s.seq);
    const text = e ? entryText(e) : null;
    const key = suggestionKey(s.seq);
    return (
      <li
        key={s.seq}
        data-change-key={key}
        className={'rounded border bg-white p-2 ' + (highlightKey === key ? 'border-violet-400 ring-2 ring-violet-200' : 'border-stone-200')}
      >
        <div className="flex items-baseline gap-1.5">
          <span className="font-mono text-[10px] text-stone-400">#{s.seq}</span>
          <span className="min-w-0 flex-1 truncate font-medium text-stone-800">{e ? describeEntry(e, log ?? undefined) : 'Förslag'}</span>
          <span className={`shrink-0 rounded border px-1 py-px text-[10px] ${STATE_CLS[s.state]}`}>{STATE_LABEL[s.state]}</span>
        </div>
        {text && <p className="mt-1 whitespace-pre-wrap text-stone-700">{truncate(text)}</p>}
        {s.state === 'stale' && (
          <p className="mt-1 text-[11px] text-amber-900">
            {s.supersededBy != null
              ? `Ersatt av en senare anpassning (#${s.supersededBy}). Förslaget kan bara avvisas.`
              : 'Texten under förslaget har ändrats. Förslaget kan bara avvisas.'}
          </p>
        )}
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-stone-500">
          <span>{authorLabel(s.author, actorId, names)}</span>
          <span>{formatAt(s.at)}</span>
          {s.decidedByAuthor && <span>· beslut av {authorLabel(s.decidedByAuthor, actorId, names)}</span>}
          <span className="ml-auto flex flex-wrap gap-2">
            <button type="button" className="underline" onClick={() => onShow(s.address)}>
              Visa
            </button>
            {decidable && s.state === 'proposed' && (
              <button type="button" className="font-medium text-emerald-800 underline disabled:opacity-50" disabled={busy} onClick={() => onDecide([s.seq], 'accept')}>
                Godta
              </button>
            )}
            {decidable && s.open && (
              <button type="button" className="font-medium text-red-800 underline disabled:opacity-50" disabled={busy} onClick={() => onDecide([s.seq], 'reject')}>
                Avvisa
              </button>
            )}
            {commentable && (
              <button type="button" className="underline" onClick={() => onComposer({ kind: 'action', seq: s.seq, changeKey: key })}>
                Kommentera
              </button>
            )}
          </span>
        </div>
        <ChangeThreads
          contractId={contractId}
          attachmentId={attachmentId}
          threads={assigned.byChange.get(key) ?? []}
          names={names}
          writable={commentable}
          composeSeq={composingOn(s.seq) ? s.seq : null}
          onCloseComposer={() => onComposer(null)}
          onShowNode={onShow}
          onChanged={onThreadsChanged}
        />
      </li>
    );
  };

  return (
    <>
      <section>
        <h3 className="mb-2 text-[11px] font-semibold tracking-wider text-red-800 uppercase">Förslag ({open.length})</h3>
        {open.length === 0 ? (
          <p className="text-stone-500">
            {suggesting ? 'Inga öppna förslag. Ändrar du texten blir ändringen ett förslag.' : 'Inga öppna förslag.'}
          </p>
        ) : (
          <ol className="flex flex-col gap-2">
            {suggestionGroups(open).map((g) => {
              const proposed = g.items.filter((s) => s.state === 'proposed').map((s) => s.seq);
              return (
                <li key={g.groupId} className={g.items.length > 1 ? 'rounded border border-red-100 bg-red-50/30 p-1.5' : ''}>
                  {g.items.length > 1 && decidable && (
                    <div className="mb-1.5 flex items-center gap-2 text-[11px]">
                      <span className="text-stone-600">{g.items.length} förslag på samma ställe</span>
                      <span className="ml-auto flex gap-2">
                        {proposed.length > 0 && (
                          <button type="button" className="font-medium text-emerald-800 underline disabled:opacity-50" disabled={busy} onClick={() => onDecide(proposed, 'accept')}>
                            Godta alla
                          </button>
                        )}
                        <button
                          type="button"
                          className="font-medium text-red-800 underline disabled:opacity-50"
                          disabled={busy}
                          onClick={() => onDecide(g.items.map((s) => s.seq), 'reject')}
                        >
                          Avvisa alla
                        </button>
                      </span>
                    </div>
                  )}
                  <ol className="flex flex-col gap-1.5">{g.items.map(suggestionItem)}</ol>
                </li>
              );
            })}
          </ol>
        )}
        {decided.length > 0 && (
          <div className="mt-2">
            <button type="button" className="text-[11px] underline" onClick={() => setShowDecided(!decidedOpen)}>
              {decidedOpen ? 'Dölj beslutade förslag' : `Visa beslutade förslag (${decided.length})`}
            </button>
            {decidedOpen && <ol className="mt-1.5 flex flex-col gap-1.5 opacity-80">{decided.map(suggestionItem)}</ol>}
          </div>
        )}
      </section>

      <section>
        <div className="mb-2 flex items-baseline gap-2">
          <h3 className="text-[11px] font-semibold tracking-wider text-stone-500 uppercase">Kommentarer</h3>
          {commentable && composer?.kind !== 'document' && (
            <button type="button" className="ml-auto text-[11px] underline" onClick={() => onComposer({ kind: 'document' })}>
              + Kommentar till dokumentet
            </button>
          )}
        </div>
        {composerFor({ kind: 'document' })}
        {composer?.kind === 'node' && (
          <div className="mb-2 rounded border border-stone-200 p-2">
            <p className="mb-1 text-[11px] text-stone-600">
              Kommentar till markerat stycke.{' '}
              <button type="button" className="underline" onClick={() => onShow(composer.address)}>
                Visa
              </button>
            </p>
            {composerFor(composer)}
          </div>
        )}
        {nodeThreads.length + docThreads.length + orphanActionThreads.length === 0 && composer?.kind !== 'document' && (
          <p className="text-stone-500">
            {assigned.byChange.size > 0 ? 'Inga övriga kommentarer – kommentarer på ändringar visas vid ändringen.' : 'Inga kommentarer på texten.'}
          </p>
        )}
        {threadList(docThreads, () => 'Dokumentet')}
        {threadList(nodeThreads, () => 'Stycke')}
        {orphanActionThreads.length > 0 && (
          <>
            <h4 className="mt-3 text-[11px] font-semibold text-stone-500">Övriga</h4>
            <p className="text-[11px] text-stone-500">Kommentarer på ändringar som inte längre finns i listan.</p>
            {threadList(orphanActionThreads, (t) => `Ändring #${t.anchor}`)}
          </>
        )}
      </section>
    </>
  );
}
